import { FC, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'antd';
import { useAppDispatch } from 'hooks/redux';
import HalfScreenModal from 'components/HalfScreenModal/HalfScreenModal';
import RichTextEditor from 'components/RichTextEditor/RichTextEditor';
import { updateCustomPageRichMediaContentAction } from 'modules/customPages/slices/customPagesSlice';

import styles from './contentCardEditor.module.less';

interface ContentCardEditorProps {
  isOpenModal: boolean;
  closeModal: () => void;
  title: string;
  content: string;
  benguideId: string;
  onSave: (title: string, content: string) => void;
  editorKey: string;
  customPageId: string;
  customPageSectionId: string;
}

const ContentCardEditor: FC<ContentCardEditorProps> = ({
  isOpenModal,
  closeModal,
  title,
  content,
  benguideId,
  onSave,
  editorKey,
  customPageId,
  customPageSectionId,
}) => {
  const dispatch = useAppDispatch();
  const [currentTitle, setCurrentTitle] = useState(title);
  const [currentContent, setCurrentContent] = useState(content);
  const [titleError, setTitleError] = useState(false);
  const textEditorRef = useRef<any>(null);

  useEffect(() => {
    setCurrentTitle(title);
    setCurrentContent(content);
    setTitleError(false);
  }, [title, content]);

  const handleSubmit = async () => {
    const trimmedTitle = currentTitle.trim();
    if (!trimmedTitle) {
      setTitleError(true);
      return;
    }

    setTitleError(false);

    try {
      await dispatch(
        updateCustomPageRichMediaContentAction(
          benguideId,
          customPageId,
          customPageSectionId,
          currentContent,
          currentTitle,
          'CONTENT_CARD',
          () => {
            // Success callback
            onSave(currentTitle, currentContent);
            closeModal();
          }
        )
      );
    } catch (error) {
      console.error('Failed to update content card:', error);
      onSave(currentTitle, currentContent);
      closeModal();
    }
  };

  const handleCancel = () => {
    setCurrentTitle(title);
    setCurrentContent(content);
    setTitleError(false);
    closeModal();
  };

  const handleEditorChange = (newContent: string) => {
    setCurrentContent(newContent);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setCurrentTitle(newTitle);
    if (titleError && newTitle.trim()) {
      setTitleError(false);
    }
  };

  return (
    <HalfScreenModal
      width={900}
      title="Edit Content Card"
      visible={isOpenModal}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      confirmModalShow={true}
      isResetEnabled={false}
      afterClose={() => {
        textEditorRef?.current?.resetUndo();
        textEditorRef?.current?.clearSelection();
      }}
    >
      <div className={styles.contentCardEditorWrapper}>
        {/* Title Section */}
        <div className={styles.titleSection}>
          <label className={styles.titleLabel}>Content Card Title*</label>
          <Input
            value={currentTitle}
            onChange={handleTitleChange}
            placeholder="Enter title..."
            maxLength={75}
            className={`${styles.titleInput} ${
              titleError ? styles.errorInput : ''
            }`}
          />
          <div className={styles.bottomRow}>
            <div className={styles.errorText}>
              {titleError ? 'Title is required' : ''}
            </div>
            <div className={styles.characterLimit}>
              Character Limit: {currentTitle.length}/75
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className={styles.contentSection}>
          <RichTextEditor
            limit={2500}
            initialValue={currentContent}
            onChange={handleEditorChange}
            defaultText={currentContent}
            ref={textEditorRef}
            masterId={benguideId}
            activateDefaultValue={false}
            editorKey={editorKey}
          />
        </div>
      </div>
    </HalfScreenModal>
  );
};

ContentCardEditor.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  benguideId: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  editorKey: PropTypes.string.isRequired,
  customPageId: PropTypes.string.isRequired,
  customPageSectionId: PropTypes.string.isRequired,
};

export default ContentCardEditor;
